import React, { useState } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CBadge,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from '@coreui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import _t from 'counterpart';
import PageLayout from '../../components/PageLayout';
import PaginationTable from '../../components/PaginationTable';
import {
  getPendingQualificationTransactions,
  getQualifiedTransactions,
  qualifyTransaction,
} from '../../services/BackendService';
import { extractErrorMessage, formatCurrency, formatDateTime, shortenId } from '../../helpers';
import { TransactionStatusBadge } from './TransactionStatusBadge';
import toast from 'react-hot-toast';
import ButtonWrapper from '../../components/ButtonWrapper';
import { IQualificationCustomer, IQualificationTransaction } from './types';

const getCustomerName = (customer: IQualificationCustomer | null | undefined): string => {
  if (!customer) return '-';
  const name = `${customer.firstname || ''} ${customer.lastname || ''}`.trim();
  return name || customer.email;
};

const QualificationPage = () => {
  const [activeTab = 'pending', setActiveTab] = useQueryParam('tab', StringParam);
  const [pendingPage, setPendingPage] = useQueryParam('pendingPage', NumberParam);
  const [qualifiedPage, setQualifiedPage] = useQueryParam('qualifiedPage', NumberParam);
  const [selectedTransaction, setSelectedTransaction] = useState<IQualificationTransaction | null>(null);

  const itemsPerPageFromLocalStorage = Number(localStorage.getItem('items_per_page_qualification_table') ?? 10);
  const [limit, setLimit] = useState<number>(itemsPerPageFromLocalStorage);

  const queryClient = useQueryClient();

  // Convert 1-based page to 0-based offset (page 1 = offset 0)
  const pendingOffset = pendingPage && pendingPage > 0 ? (pendingPage - 1) * limit : 0;
  const qualifiedOffset = qualifiedPage && qualifiedPage > 0 ? (qualifiedPage - 1) * limit : 0;

  const pendingQuery = useQuery(
    ['pending-qualification', pendingPage, limit],
    () => getPendingQualificationTransactions(limit, pendingOffset),
    { keepPreviousData: true }
  );

  const qualifiedQuery = useQuery(
    ['qualified-transactions', qualifiedPage, limit],
    () => getQualifiedTransactions(limit, qualifiedOffset),
    { keepPreviousData: true }
  );

  const qualifyMutation = useMutation((transactionId: string) => qualifyTransaction(transactionId), {
    onSuccess: (result) => {
      let message = _t('qualification.transaction-qualified');
      if (result.cpaAwarded) {
        message += ' ' + _t('qualification.cpa-awarded');
      }
      if (result.affiliateFTDSet) {
        message += ' ' + _t('qualification.affiliate-ftd-set');
      }
      toast.success(message);
      queryClient.invalidateQueries('pending-qualification');
      queryClient.invalidateQueries('qualified-transactions');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error));
    },
  });

  const openQualifyModal = (transaction: IQualificationTransaction) => {
    setSelectedTransaction(transaction);
  };

  const closeModal = () => {
    setSelectedTransaction(null);
  };

  const handleConfirm = () => {
    if (!selectedTransaction) return;
    qualifyMutation.mutate(selectedTransaction.id);
  };

  const onPendingPageChanged = (page: number) => {
    setPendingPage(page, 'replaceIn');
  };

  const onQualifiedPageChanged = (page: number) => {
    setQualifiedPage(page, 'replaceIn');
  };

  const onLimitChanged = async (newLimit: number) => {
    setLimit(newLimit);
    localStorage.setItem('items_per_page_qualification_table', newLimit.toString());
    // Reset pages when changing limit
    setPendingPage(1, 'replaceIn');
    setQualifiedPage(1, 'replaceIn');
    await queryClient.invalidateQueries('pending-qualification');
    await queryClient.invalidateQueries('qualified-transactions');
  };

  const handleTabChange = (tab: 'pending' | 'qualified') => {
    setActiveTab(tab, 'replaceIn');
  };

  const pendingTableFields = [
    { key: 'id', label: _t('global.id') },
    { key: 'customer', label: _t('global.customer') },
    { key: 'amount', label: _t('global.amount') },
    { key: 'type', label: _t('global.type') },
    { key: 'status', label: _t('global.status') },
    { key: 'createdAt', label: _t('global.created-at') },
    { key: 'actions', label: _t('action.action') },
  ];

  const qualifiedTableFields = [
    { key: 'id', label: _t('global.id') },
    { key: 'customer', label: _t('global.customer') },
    { key: 'amount', label: _t('global.amount') },
    { key: 'qualifiedBy', label: _t('qualification.qualified-by') },
    { key: 'qualifiedAt', label: _t('qualification.qualified-at') },
  ];

  const pendingScopedSlots = {
    id: (item: IQualificationTransaction) => (
      <td>
        <Link to={`/transactions/${item.id}`}>{shortenId(item.id)}</Link>
      </td>
    ),
    customer: (item: IQualificationTransaction) => {
      const customer = item.wallet?.customer;
      return <td>{customer ? <Link to={`/customers/${customer.id}`}>{getCustomerName(customer)}</Link> : '-'}</td>;
    },
    amount: (item: IQualificationTransaction) => <td>{formatCurrency(item.amount, item.currency)}</td>,
    type: (item: IQualificationTransaction) => (
      <td>{_t(`transactionTypes.${item.type}`, { fallback: item.type })}</td>
    ),
    status: (item: IQualificationTransaction) => (
      <td>
        <TransactionStatusBadge status={item.status} />
      </td>
    ),
    createdAt: (item: IQualificationTransaction) => <td>{formatDateTime(item.createdAt)}</td>,
    actions: (item: IQualificationTransaction) => (
      <td>
        <CButton color="success" size="sm" onClick={() => openQualifyModal(item)}>
          {_t('qualification.qualify')}
        </CButton>
      </td>
    ),
  };

  const qualifiedScopedSlots = {
    id: (item: IQualificationTransaction) => (
      <td>
        <Link to={`/transactions/${item.id}`}>{shortenId(item.id)}</Link>
      </td>
    ),
    customer: (item: IQualificationTransaction) => {
      const customer = item.wallet?.customer;
      return <td>{customer ? <Link to={`/customers/${customer.id}`}>{getCustomerName(customer)}</Link> : '-'}</td>;
    },
    amount: (item: IQualificationTransaction) => <td>{formatCurrency(item.amount, item.currency)}</td>,
    qualifiedBy: (item: IQualificationTransaction) => (
      <td>
        {item.qualifiedBy ? (
          <Link to={`/customers/${item.qualifiedBy.id}`}>{getCustomerName(item.qualifiedBy)}</Link>
        ) : (
          '-'
        )}
      </td>
    ),
    qualifiedAt: (item: IQualificationTransaction) => (
      <td>{item.qualifiedAt ? formatDateTime(item.qualifiedAt) : '-'}</td>
    ),
  };

  const pendingCount = pendingQuery.data?.total || 0;
  const qualifiedCount = qualifiedQuery.data?.total || 0;
  const pendingPages = pendingCount > 0 ? Math.ceil(pendingCount / limit) : 0;
  const qualifiedPages = qualifiedCount > 0 ? Math.ceil(qualifiedCount / limit) : 0;

  return (
    <PageLayout title={_t('qualification.title')}>
      <CCard>
        <CCardHeader>
          <CNav variant="tabs">
            <CNavItem>
              <CNavLink
                active={activeTab === 'pending'}
                onClick={() => handleTabChange('pending')}
                style={{ cursor: 'pointer' }}
              >
                {_t('qualification.pending-qualification')} <CBadge color="warning">{pendingCount}</CBadge>
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={activeTab === 'qualified'}
                onClick={() => handleTabChange('qualified')}
                style={{ cursor: 'pointer' }}
              >
                {_t('qualification.qualified-transactions')} <CBadge color="success">{qualifiedCount}</CBadge>
              </CNavLink>
            </CNavItem>
          </CNav>
        </CCardHeader>
        <CCardBody>
          <CTabContent>
            <CTabPane active={activeTab === 'pending'}>
              <PaginationTable
                data={pendingQuery.data?.transactions || []}
                tableFields={pendingTableFields}
                scopedSlots={pendingScopedSlots}
                pages={pendingPages}
                activePage={pendingPage || 1}
                onPageChanged={onPendingPageChanged}
                loading={pendingQuery.isLoading}
                pagination
                itemsPerPage={limit}
                onLimitChanged={onLimitChanged}
              />
            </CTabPane>
            <CTabPane active={activeTab === 'qualified'}>
              <PaginationTable
                data={qualifiedQuery.data?.transactions || []}
                tableFields={qualifiedTableFields}
                scopedSlots={qualifiedScopedSlots}
                pages={qualifiedPages}
                activePage={qualifiedPage || 1}
                onPageChanged={onQualifiedPageChanged}
                loading={qualifiedQuery.isLoading}
                pagination
                itemsPerPage={limit}
                onLimitChanged={onLimitChanged}
              />
            </CTabPane>
          </CTabContent>
        </CCardBody>
      </CCard>

      {/* Qualification Confirmation Modal */}
      <CModal show={Boolean(selectedTransaction)} onClose={closeModal}>
        <CModalHeader closeButton>
          <CModalTitle>{_t('qualification.confirm-title')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedTransaction && (
            <>
              <p>
                <strong>{_t('global.customer')}:</strong> {getCustomerName(selectedTransaction.wallet?.customer)}
              </p>
              <p>
                <strong>{_t('global.amount')}:</strong>{' '}
                {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
              </p>
              <p>
                <strong>{_t('global.created-at')}:</strong> {formatDateTime(selectedTransaction.createdAt)}
              </p>

              <div className="alert alert-danger mt-3">
                <strong>{_t('qualification.warning-title')}</strong>
                <p className="mb-0 mt-2">{_t('qualification.irreversible-warning')}</p>
              </div>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <ButtonWrapper color="success" onClick={handleConfirm} isLoading={qualifyMutation.isLoading}>
            {_t('qualification.confirm-qualify')}
          </ButtonWrapper>
          <CButton color="light" variant="outline" onClick={closeModal} disabled={qualifyMutation.isLoading}>
            {_t('action.cancel')}
          </CButton>
        </CModalFooter>
      </CModal>
    </PageLayout>
  );
};

export default QualificationPage;
